import httpClient from '@/api/http-client';
import getFormData from '@/util/form-data';
import clone from 'just-clone';

const endpoint = 'api/permits';

const getPermitFormData = (permit) => {
  const fieldBlacklist = ['accompanying_user', 'subject', 'selected_user', 'permit_fields', 'user'];

  if (permit.applicant_user_id) {
    fieldBlacklist.push('applicant_user');
  }

  const permitCopy = clone(permit);

  permitCopy.permit_field_data = permitCopy.permit_fields?.map((field) => field.permit_field_data);

  const formData = getFormData(permitCopy, fieldBlacklist);
  if (!permit.new_documents?.length) {
    // to receive document validation messages, when there's no files selected
    formData.append('new_documents[]', '');
  }
  return formData;
};

const permitService = {
  getPage: (params) => httpClient.get(endpoint, { params }),

  getById: (id) => httpClient.get(`${endpoint}/${id}`),

  search: (search) => httpClient.post(`${endpoint}/find`, { search }),

  update: (permit) => {
    const permitCopy = clone(permit);
    if (permit.is_multiple_permit) {
      permitCopy.applicant_names = permitCopy.applicant_names.split('\n');
    }
    const formData = getPermitFormData(permitCopy);
    formData.append('_method', 'put');
    return httpClient.post(`${endpoint}/${permit.id}`, formData);
  },

  approve: (permit) => httpClient.put(`${endpoint}/${permit.id}`, permit),

  checkPermit: (params) => httpClient.post(`${endpoint}/check-permit`, params),

  getCaaDocument: (payload) =>
    httpClient({
      url: 'api/generate-caa-document',
      method: 'POST',
      responseType: 'blob',
      data: payload,
    }),

  getSimplifiedCaaDocument: (payload) =>
    httpClient({
      url: 'api/generate-simplified-caa-document',
      method: 'POST',
      responseType: 'blob',
      data: payload,
    }),

  exportPermitPdfs: (permitIds) =>
    httpClient({
      url: 'api/download-permits',
      method: 'GET',
      responseType: 'blob',
      params: { permit_ids: permitIds },
    }),

  getAgreementDocument: (permit) =>
    httpClient({
      url: `${endpoint}/${permit.applicant_user_id}/generate-agreement-document`,
      method: 'GET',
      responseType: 'blob',
    }),

  applicantEncodedPhotoUpload: (permit) =>
    httpClient.post(`${endpoint}/${permit.id}/applicant-photo-encoded`, {
      applicant_photo: permit.applicant_photo,
    }),

  applicantPhotoUpload: (permit) => {
    const formData = new FormData();
    formData.append('applicant_photo', permit.applicant_photo);
    return httpClient.post(`${endpoint}/${permit.id}/applicant-photo`, formData);
  },

  delete: (permit) => httpClient.delete(`${endpoint}/${permit.id}`),

  createMultiple: (permit) => {
    const fieldBlacklist = [
      'accompanying_user',
      'subject',
      'selected_user',
      'permit_fields',
      'user',
    ];
    const permitCopy = clone(permit);

    permitCopy.permit_field_data = permitCopy.permit_fields?.map(
      (field) => field.permit_field_data,
    );

    const formData = getFormData(permitCopy, fieldBlacklist);
    if (!permit.new_documents?.length) {
      // to receive document validation messages, when no files is selected
      formData.append('new_documents[]', '');
    }
    return httpClient.post('api/multiple-temporary-permits', formData);
  },

  create: (permit) => {
    const formData = getPermitFormData(permit);
    if (permit.status === 'permit_issued') {
      formData.append('permit_issued_status', 'valid');
    }
    return httpClient.post(`${endpoint}`, formData);
  },

  getParkingInfoPage: (params) =>
    httpClient.get(`${endpoint}/need-approve-parking-info`, { params }),

  approveParkingInfo: (permit) =>
    httpClient.put(`${endpoint}/${permit.id}/approve-parking-info`, permit),

  sendInvitationEmail: (permit) =>
    httpClient.post(`${endpoint}/${permit.id}/send-permit-ready-email`),

  getValidation: (permit) =>
    httpClient.post(`${endpoint}/living-and-working-places-validation`, permit),

  getPermitReport: (params) => httpClient.get('api/permit-report', { params }),

  getPermitPdfReport: (permitId) =>
    httpClient({
      url: `api/generate-permit-report/${permitId}`,
      method: 'GET',
      responseType: 'blob',
    }),

  getPermitAsPdf: (permitId) =>
    httpClient({
      url: `api/documents/download-permit/${permitId}`,
      method: 'GET',
      responseType: 'blob',
    }),

  uploadPermitImages: (payload) =>
    httpClient.post('api/add-photos-to-existing-permits', getFormData(payload)),

  getPermanentVehiclePermits: () => httpClient.get('api/permanent-vehicle-permits'),

  suspendVehiclePermits: (ids) =>
    httpClient.post('api/suspend-permanent-vehicle-permits', { permit_ids: ids }),

  updateIssuedPermitStatus: (payload) =>
    httpClient.post(`${endpoint}/${payload.permitId}/issued-permit-status`, {
      status: payload.status,
      not_valid_reason_type: payload.not_valid_reason_type,
      last_working_day_date: payload.last_working_day_date,
    }),

  updateZoning: (payload) => httpClient.patch(`${endpoint}/update-zones-and-airports`, payload),

  getPermitScannerSettings: (code) =>
    httpClient.get('api/permit-scanner-settings', { params: { code } }),

  updateEndAt: (payload) =>
    httpClient.patch(`${endpoint}/${payload.permit_id}/end-at`, { end_at: payload.end_at }),

  getOldPermit: (permitId) => httpClient.get(`${endpoint}/${permitId}/get-user-old-permit`),

  getChangeHistory: async (permitId) => httpClient.get(`${endpoint}/${permitId}/changes-history`),

  exportPermitPersonData: (permitIds) =>
    httpClient({
      url: 'api/export-permit-person-data',
      method: 'POST',
      data: { permit_ids: permitIds },
      responseType: 'blob',
    }),

  exportPermitReportByFilter: (params) =>
    httpClient({
      url: 'api/export-permit-report-by-filter',
      method: 'POST',
      data: params,
      responseType: 'blob',
    }),

  getStatus: async (permitId) => httpClient.get(`${endpoint}/${permitId}/status`),

  bulkMarkReputationAsImpeccable: (permitIds, reputationValidUntil) =>
    httpClient.patch(`${endpoint}/bulk-reputation`, {
      permit_ids: permitIds,
      reputation_valid_until: reputationValidUntil,
    }),
};

export default permitService;
